<!-- SubscriptionList.vue -->
<template>
    <div class="payment-container">
        <span class="payment-title">{{ $t('m.subsTitle') }}</span>
        <div
            v-active-touch
            class="subscription-content interactive-surface"
            :class="{
                'list-style': isMoreThanOneLi,
                active: defaultSelected
            }"
            @click="$emit('switchToDefault')"
        >
            <template v-if="defaultSubscriptionDetail">
                <span class="title">{{ $t('m.default') }}</span>
                <pre class="detail">{{ defaultSubscriptionDetail }}</pre>
                <span v-if="billingRuleTaxText" class="tax-hint">{{ billingRuleTaxText }}</span>
            </template>
            <template v-else>
                <span
                    class="billing-rule-item"
                    v-for="(item, index) in formatBillingRule(billingRule)"
                    :key="index"
                >
                    <span v-if="item.price" class="price">{{ item.price }}</span>
                    <span v-if="item.priceTime" class="price-time">
                        <span class="price-time-slash">/</span>
                        <span class="price-time-label">{{ stripPriceTimeSlash(item.priceTime) }}</span>
                    </span>
                    <span v-if="item.message" class="message">{{ item.message }}</span>
                    <span v-if="item.li" class="li">{{ item.li }}</span>
                    <span v-if="item.taxHint" class="tax-hint">{{ item.taxHint }}</span>
                </span>
            </template>
        </div>
        <div
            v-active-touch
            class="subscription-content subs-and-save interactive-surface"
            v-for="item in subscriptionList" :key="item.id"
            :class="{active: item.checked}"
            @click="$emit('switchSubscription', item)">
            <span class="badge">{{ $t('m.subscribeAndSave') }}</span>
            <span  class="title">{{ item.display_name }}</span>
            <pre v-if="item.detail" class="detail">{{ item.detail }}</pre>
            <div v-else class="list-style">
                <span class="li" v-for="(detail, index) in formatSubscriptionDetail(
                    item.discount_type, 
                    item.discount_value, 
                    item.duration_limit, 
                    currencyFormatBillingRule(billingRule.price, billingRule, billingRule.currency), 
                    billingRule.price_time, 
                    billingRule.top_limit ? currencyFormatBillingRule(billingRule.top_limit, billingRule, billingRule.currency) : undefined)" 
                    :key="index">
                    {{ detail }}
                </span>
            </div>
            <span v-if="billingRuleTaxText" class="tax-hint">{{ billingRuleTaxText }}</span>
            <div class="subscription-item-bottom">
                <label
                    v-active-touch
                    class="subscription-auto-renew interactive-text"
                    @touchstart.stop
                    @mousedown.stop
                    @click="$emit('switchSubscriptionAutoRenew', {item, $event})"
                >
                    <input type="checkbox" v-model="item.auto_renew">
                    <span class="slider"></span>
                    <span class="label-text">{{ $t('m.autoRenew') }}</span>
                </label>
                <span class="subscription-item-price"><strong>{{currencyFormat(item.price, item.currency)}}</strong>/{{ item.price_period }}</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        billingRule: {
            type: Object,
            required: true
        },
        subscriptionList: {
            type: Array,
            required: true
        },
        defaultSubscriptionDetail: {
            type: String,
            default: ''
        },
        defaultSelected: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        billingRuleTaxText() {
            return this.getBillingRuleTaxText(this.billingRule)
        },
        isMoreThanOneLi() {
            // Move from main computed
            const filtered = Object.entries(this.billingRule).filter(([key, rule]) => {
                if (!rule) return false;
                const fields = ['low_limit', 'top_limit', 'pre_auth_amount'];
                const nonZeroCount = fields.filter(field => rule[field] !== 0).length;
                const isDepositValid = rule.deposit !== 0 && rule.maximum_rental_time !== 0;

                return nonZeroCount > 0 || isDepositValid;
            });

            return filtered.length > 1;
        }
    },
    methods: {
        stripPriceTimeSlash(value) {
            return String(value || '').replace(/^\/\s*/, '')
        },
    },
}
</script>
<style lang="scss">
@use '@/styles/variables.scss' as *;

.payment-container {
  margin-top: 20px;
  display: flex;
  gap: 9px;
  flex-direction: column;
  align-items: center;

  .payment-title {
    display: inline-block;
    width: $container-width;
    text-align: left;
    font-size: 16px;
    font-weight: 600;
    line-height: 19.09px;
    letter-spacing: -0.2px;
  }
}

.subscription-content {
  width: $container-width;
  background: $background-white;
  padding: 25px 15px 20px;
  border-radius: $border-radius;
  font-family: $font-family;
  font-weight: 400;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  color: $secondary-color;
  box-shadow: $shadow;
  position: relative;
  border: 1px solid transparent;
  line-height: 144%;

  .title {
    font-weight: 600;
    font-size: 16px;
    color: $text-color;
    margin-bottom: 10px;
  }

  .detail {
    white-space: pre-wrap;
    margin: 0;
    font-family: $font-family;
    line-height: 155%;
    color: $secondary-color;
    font-size: 14px;
  }

  &.list-style {
    padding: 20px 15px;
  }

  &.active {
    background: var(--theme-selectable-accent-bg);
    border-color: var(--theme-selectable-accent-color);
    color: var(--theme-selectable-accent-color);

    .title {
      color: var(--theme-selectable-accent-color);
    }

    .billing-rule-item {
      color: var(--theme-selectable-accent-color);

      .price {
        color: var(--theme-selectable-accent-color);
      }

      .li::before {
        background-color: var(--theme-selectable-accent-color);
      }
    }

    .badge {
      background-color: var(--theme-selectable-accent-color);
      color: $background-white;
      box-shadow: none;
    }

    .subscription-item-price {
      strong {
        color: var(--theme-selectable-accent-color);
      }
    }
  }

  .tax-hint {
    font-size: 12px;
    line-height: 17px;
    color: #8a8a8a;
  }

  &.subs-and-save {
    .list-style {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .tax-hint {
      width: 100%;
      position: relative;
      display: inline-block;
      padding-left: 14px;

      &::before {
        content: '';
        position: absolute;
        left: 1px;
        top: 6px;
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background-color: $secondary-color;
      }
    }
  }
}

.billing-rule-item {
  line-height: 20.16px;
  font-size: 14px;
  color: $secondary-color;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;

  .price {
    font-family: $font-family-display;
    font-weight: 700;
    font-size: 20px;
    line-height: 35px;
    color: $text-color;
    letter-spacing: 0.02em;
  }

  .price-time {
    display: inline-flex;
    align-items: baseline;
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
    color: $text-color;

    .price-time-slash {
      margin: 0 2px;
    }
  }
}

.badge {
  position: absolute;
  top: -1px;
  right: -1px;
  background-color: var(--theme-selectable-accent-bg);
  color: var(--theme-selectable-accent-color);
  font-size: 10px;
  font-weight: 500;
  padding: 4px 10px;
  border-bottom-left-radius: $border-radius;
  border-top-right-radius: $border-radius;
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.07);
}

.subscription-item-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  margin-top: 10px;
  gap: 10px;
}

.subscription-auto-renew {
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  color: $secondary-color;
  width: fit-content;

  input {
    display: none;
  }

  .slider {
    width: 40px;
    height: 22px;
    background-color: #ccc;
    border-radius: 999px;
    position: relative;
    transition: background-color 0.3s;
  }

  .slider::before {
    content: "";
    position: absolute;
    left: 2px;
    top: 2px;
    width: 18px;
    height: 18px;
    background-color: $background-white;
    border-radius: 50%;
    transition: transform 0.3s;
  }

  input:checked + .slider {
    background-color: var(--theme-selectable-accent-color);
  }

  input:checked + .slider::before {
    transform: translateX(18px);
  }

  input:checked ~ .label-text {
    color: var(--theme-selectable-accent-color);
  }

  .label-text {
    letter-spacing: -0.4px;
  }
}

.subscription-item-price {
  color: $secondary-color;

  strong {
    color: $text-color;
    font-size: 18px;
    letter-spacing: -0.4px;
  }
}

.list-style {
  .li {
    width: 100%;
    position: relative;
    display: inline-block;
    padding-left: 14px;
    font-size: 12px;
    font-weight: 400;
    line-height: 17.28px;

    &::before {
      content: '';
      position: absolute;
      left: 1px;
      top: 6px;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background-color: $secondary-color;
    }
  }
}
</style>
